import { useState, useEffect } from "react";
import { KeyRound, Save, FlaskConical } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi, adminApi } from "@/lib/api";
import { toast } from "sonner";

function ProductSandboxCard() {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => { adminApi.getProductSettings().then((r) => setEnabled(r.data.sandbox_enabled)).catch(() => setEnabled(false)); }, []);
  const toggle = async (v) => {
    try {
      const r = await adminApi.setProductSettings(v);
      setEnabled(r.data.sandbox_enabled);
      toast.success(v ? "Product team now sees the sandbox (synthetic data)" : "Product team now sees live data (web-indexing hidden)");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6" data-testid="product-sandbox-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
            <FlaskConical size={20} className="text-violet-600" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Product team sandbox</h3>
            <p className="text-xs text-slate-400 max-w-sm">Off: Product users see live data with web-indexing revenue hidden. On: they see the synthetic practice dataset instead.</p>
          </div>
        </div>
        <Switch checked={!!enabled} disabled={enabled === null} onCheckedChange={toggle} data-testid="product-sandbox-switch" />
      </div>
      <p className="text-xs mt-3 font-medium text-slate-600" data-testid="product-sandbox-status">Current mode: {enabled ? "Sandbox (synthetic)" : "Live data (web-indexing hidden)"}</p>
    </div>
  );
}

export default function AdminSettings() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-xl" data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">Manage your admin account settings</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <KeyRound size={20} className="text-blue-600" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Change Password</h3>
            <p className="text-xs text-slate-400">Update your admin login password</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Current Password</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              placeholder="Enter current password"
              data-testid="current-password-input"
              className="h-10 border-slate-200"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">New Password</Label>
            <Input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="Enter new password"
              data-testid="new-password-input"
              className="h-10 border-slate-200"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Confirm New Password</Label>
            <Input
              type="password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
              className="h-10 border-slate-200"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="change-password-btn"
            className="h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all active:scale-95"
          >
            {saving ? "Saving..." : "Change Password"}
            {!saving && <Save size={16} className="ml-2" />}
          </Button>
        </form>
      </div>
      <ProductSandboxCard />
    </div>
  );
}
